import { useState, useCallback } from 'react';
import {
  fetchUnits,
  createUnit,
  updateUnit,
  deleteUnit,
  reorderDefaultUnits,
  type UnitInput,
} from '../services/master.service';
import type { Unit } from '../types';

export function useUnit() {
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadUnits = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setUnits(await fetchUnits());
    } catch (e) {
      setError(e instanceof Error ? e.message : '単位の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  const addUnit = useCallback(async (data: UnitInput): Promise<Unit> => {
    const created = await createUnit(data);
    setUnits((prev) => [created, ...prev]);
    return created;
  }, []);

  const editUnit = useCallback(async (id: string, data: Partial<UnitInput>): Promise<Unit> => {
    const updated = await updateUnit(id, data);
    setUnits((prev) => prev.map((x) => (x.id === id ? updated : x)));
    return updated;
  }, []);

  const removeUnit = useCallback(async (id: string): Promise<void> => {
    await deleteUnit(id);
    setUnits((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const reorderDefaults = useCallback(async (orderedIds: string[]): Promise<void> => {
    // 楽観的更新：先に並び替えを反映し、失敗時は再取得でロールバックする
    setUnits((prev) => {
      const map = new Map(prev.map((u) => [u.id, u]));
      const defaults = orderedIds
        .map((id) => map.get(id))
        .filter((u): u is Unit => Boolean(u));
      const customs = prev.filter((u) => !orderedIds.includes(u.id));
      return [...defaults, ...customs];
    });
    try {
      await reorderDefaultUnits(orderedIds);
    } catch (e) {
      setError(e instanceof Error ? e.message : '並び替えに失敗しました');
      await loadUnits();
    }
  }, [loadUnits]);

  return { units, loading, error, loadUnits, addUnit, editUnit, removeUnit, reorderDefaults };
}
